import {
  Button,
  Col,
  Form,
  message,
  Popover,
  Row,
  Select,
  Spin,
  Tag,
  Tooltip,
} from "antd";
import { isEqual, sortBy } from "lodash";
import moment from "moment";
import { useCallback, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import evaluationTypes from "../../../utils/evaluationType.json";
import codes from '../../../utils/providerCodes.json';
import History from '../../components/History';
import usePaymentMethods from "../hooks/usePaymentMetods";
import useUpdatePaymentMethod from "../hooks/useUpdatePaymentMethod";
import SummaryServices from "./SummaryServices";

const { Option } = Select;
const { format } = require("rut.js");

const AdmissionDetails = ({
  id,
  reservation,
  setReservation,
  setStartAdmission,
  admittedPatients,
  countryCode,
  laboratoryModule,
  evaluationTypeOnFinish,
  loading,
  closeEvaluationTypePopover,
}) => {
  const [form] = Form.useForm();
  const { [form]: evaluationTypeForm } = Form.useForm();
  const navigate = useNavigate();
  const paymentMethods = usePaymentMethods();
  const updatePaymentMethods = useUpdatePaymentMethod();
  const { t } = useTranslation();

  const historyParams = {
    data: { type: 'admission', id: reservation?.id },
  };

  const validatePaymentMethods = useCallback(async () => {
    const options = await paymentMethods.getPaymentData(reservation);
    const defaultValue = options?.find((option) => option.default_method);
    if (!reservation?.payment_methods?.length && defaultValue) {
      form.setFieldsValue({
        paymentMethods: [defaultValue.id],
      });
    } else if (options) {
      let ids = reservation.payment_methods;
      if (reservation.payment_methods_codes?.includes(codes.sucursal)) {
        const sucursalId = options.find((option) => option?.code?.toLowerCase() === codes.sucursal)
        ids = ids.filter(id => id !== sucursalId?.id);
      }
      form.setFieldsValue({
        paymentMethods: ids,
      });
    }
  }, [reservation]);

  useEffect(() => {
    if (reservation?.company_id && reservation?.payment_methods) {
      validatePaymentMethods();
    }
  }, [validatePaymentMethods]);

  const formatDate = (date) => {
    return moment(date, "YYYY-MM-DD").format("DD-MM-YYYY");
  };

  const alignmentSettings = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };

  const handleBack = () => {
    navigate(-1);
  }

  const handleStartAdmission = async () => {
    try {
      const value = form.getFieldValue('paymentMethods');
      const params = {
        reservation_id: reservation.id,
        payment_methods: value,
      };

      if (isEqual(sortBy(reservation.payment_methods), sortBy(value))) {
        setStartAdmission(true);
        setReservation((reservation) => {
          return { ...reservation, payment_methods: value };
        });
        return;
      }

      await updatePaymentMethods.submit(params);

      setReservation((reservation) => {
        return { ...reservation, payment_methods: value };
      });
      setStartAdmission(true);
    } catch (err) {
      message.error({
        content: `Error al guardar los métodos de pago`,
        style: {
          marginTop: "35vh",
          fontSize: 16,
        },
      });
      console.error(err);
    }
  }



  return (
    <Row justify="center" className="admission-details">
      {
        reservation &&
        <Col lg={24} xs={24} className="admission-details__header pt-30 pb-10">
          <Row justify="center">
            <Col lg={22} xs={22}>
              <Row justify="space-between">
                <Col className="back-button-wrapper">
                  <Button onClick={handleBack} type="link">
                    <i className="icon-system-chevron-left" />
                    Volver
                  </Button>
                </Col>
                <Col>
                  <Button
                    onClick={() => form.submit()}
                    loading={updatePaymentMethods.isLoading}
                    className="cmt-button primary"
                    type="primary"
                    size="large"
                  >
                    Comenzar admisión
                  </Button>
                </Col>
                <Col lg={24} xs={24}>
                  <h1 className="text-center mt-lg-0 mt-md-30 mt-sm-30 mt-xs-30">
                    {t('admissionPage.admissionDetails.title')} {reservation.number}
                  </h1>
                </Col>
              </Row>
              <Row justify="center">
                <Col xxl={14} xl={15} lg={16} xs={24}>
                  <Row>
                    <Col lg={12} xs={24}>
                      <p className="mb-20">
                        Compañía:
                        <strong className="ml-10">{reservation.company.business_name}</strong>
                      </p>
                    </Col>
                    <Col lg={12} xs={24}>
                      <p className="mb-20">
                        Total de trabajadores:
                        <strong className="ml-10">{reservation.total_patients}</strong>
                      </p>
                    </Col>
                    <Col lg={12} xs={24}>
                      <p className="mb-20">
                        Fecha de reserva:
                        <strong className="ml-10">{formatDate(reservation.reservation_date)}</strong>
                      </p>
                    </Col>
                    <Col lg={12} xs={24}>
                      <Row>
                        <Col>
                          <p className="mb-20 mr-10">Bloques:</p>
                        </Col>
                        <Col>
                          {
                            reservation?.blocks?.map((block, index) => {
                              return (
                                <Tag key={index}>{block}</Tag>
                              )
                            })
                          }
                        </Col>
                      </Row>
                    </Col>
                    <Col lg={14} xs={24}>
                      <Form className='cmt-form' form={form} onFinish={handleStartAdmission} layout='horizontal' size='large'>
                        {
                          paymentMethods?.isLoading ?
                            <Spin className="cmt-spin" />
                            :
                            <Form.Item
                              name="paymentMethods"
                              label="Método de pago:"
                              rules={[{
                                required: true,
                                message: 'Método de pago es requerido'
                              }]}
                            >
                              <Select
                                mode='multiple'
                                className='cmt-select'
                                placeholder="Seleccione método de pago"
                                id='paymentMethods'
                                disabled={
                                  (reservation?.payment_methods_codes?.includes(codes.mercadopagocl) || reservation?.payment_methods_codes?.includes(codes.toku))
                                  && reservation?.paid
                                }
                              >
                                {
                                  paymentMethods?.data?.map((method, index) =>
                                    <Option
                                      key={index}
                                      value={method.id}
                                      disabled={method.code === codes.toku}
                                    >
                                      {method.name}
                                    </Option>
                                  )
                                }
                              </Select>
                            </Form.Item>
                        }
                      </Form>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row justify="end">
                <Col xl={4} lg={5} xs={24}>
                  <Row gutter={[0, 15]}>
                    <Col lg={24} xs={24}>
                      <History
                        params={historyParams}
                      />
                    </Col>
                    <Col lg={24} xs={24}>
                      <Link to={`/reservas/${reservation.id}/ver`}>
                        <Button className="cmt-button secondary" type="primary">
                          Ver detalle
                        </Button>
                      </Link>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      }

      <Col lg={22} xs={24} className="mb-lg-20 mb-xs-40 mt-lg-20 mt-md-50 mt-sm-50 mt-xs-50">
        <Row className="services-wrapper">
          <Col lg={14} xs={22}>
            <SummaryServices id={id} />
          </Col>
        </Row>
      </Col>

      {
        admittedPatients && admittedPatients.length > 0 &&
        <Col lg={22} xs={22} className="admission-details__patients mb-50">
          <Row>
            <h2 className="mb-20">
              {t('admissionPage.admissionDetails.patientsDetails')}
            </h2>
          </Row>
          <Row className="header-table">
            <Col lg={(laboratoryModule) ? 6 : 7} xs={6}>
              <p className="title ml-15 mb-5">
                {t('admissionPage.admissionDetails.patient')}
              </p>
            </Col>
            <Col lg={(laboratoryModule) ? 6 : 7} xs={6}>
              <p className="title text-center mb-5">Identificador</p>
            </Col>
            <Col lg={(laboratoryModule) ? 4 : 5} xs={4}>
              <p className="title text-center mb-5">{t('admissionPage.admissionDetails.services')}</p>
            </Col>
            <Col lg={(laboratoryModule) ? 4 : 5} xs={4}>
              <p className="title text-center mr-15 mb-5">{t('admissionPage.admissionDetails.hiredOrServiceType')}</p>
            </Col>
            {
              (laboratoryModule) &&
              <Col lg={4} xs={4}>
                <p className="title text-center mb-5">Laboratorio</p>
              </Col>
            }
          </Row>
          {
            (loading.patients) ?
              <Spin className="cmt-spin" />
              : admittedPatients.map((item, index) => {
                return (
                  <Row key={index} className='row-item pt-15 pb-15 mb-10'>
                    <Col
                      lg={(laboratoryModule) ? 6 : 7} xs={12}
                      className="column"
                    >
                      <p className="title ml-lg-15 mb-5">
                        {t('admissionPage.admissionDetails.patient')}
                      </p>
                      <p className="ml-lg-15 mb-0">{item.user_fullname}</p>
                    </Col>
                    <Col
                      lg={(laboratoryModule) ? 6 : 7} xs={12}
                      className="column"
                      style={{ ...alignmentSettings }}
                    >
                      <p className="title text-center mb-5">Identificador</p>
                      <p className="text-center mb-0">
                        {
                          item.document_type === 'rut' ? format(item.document_number) : item.document_number
                        }
                      </p>
                    </Col>

                    <Col
                      lg={(laboratoryModule) ? 4 : 5} xs={12}
                      className="column mt-lg-0 mt-xs-10"
                      style={{ ...alignmentSettings }}
                    >
                      <p className="title text-center mb-5">
                        {t('admissionPage.admissionDetails.services')}
                      </p>
                      <Tooltip
                        trigger="click"
                        color="#FFFFFF"
                        style={{ width: 100 }}
                        title={
                          <Row>
                            <Col lg={24} xs={24}>
                              <p className='title mb-10'>
                                {t('admissionPage.admissionDetails.services')}
                              </p>
                            </Col>
                            {
                              item.services.map((service, index) => {
                                return (
                                  <Col lg={24} xs={24} key={index}>
                                    <p className='mb-5'>{service.name}</p>
                                  </Col>
                                )
                              })
                            }
                          </Row>
                        }
                      >
                        <p className="text-center mb-0" style={{ cursor: 'pointer' }}>
                          Ver {t('admissionPage.admissionDetails.services').toLocaleLowerCase()}
                        </p>
                      </Tooltip>
                    </Col>
                    <Col
                      lg={(laboratoryModule) ? 4 : 5} xs={12}
                      className="column mt-lg-0 mt-xs-10"
                      style={{ ...alignmentSettings }}
                    >
                      <p className="title text-center mr-15 mb-5">
                        {t('admissionPage.admissionDetails.hiredOrServiceType')}
                      </p>
                      <Popover
                        title={t('admissionPage.admissionDetails.hiredOrServiceType')}
                        trigger="click"
                        content={
                          <Form form={evaluationTypeForm} onFinish={(values) => evaluationTypeOnFinish(values, item)}>
                            <Row>
                              <Col lg={24} xs={24}>
                                <Form.Item
                                  name="evaluationType"
                                  style={{ width: '100%' }}
                                >
                                  <Select
                                    className='cmt-select'
                                    placeholder='Seleccione una opción'
                                    size="middle">
                                    {
                                      evaluationTypes[countryCode]?.map((evaluationType) =>
                                        <Select.Option value={evaluationType.value}>
                                          {evaluationType.text}
                                        </Select.Option>
                                      )
                                    }
                                  </Select>
                                </Form.Item>
                              </Col>
                            </Row>
                            <Row gutter={10}>
                              <Col>
                                <Button
                                  className="cmt-button primary"
                                  type="primary"
                                  htmlType="submit"
                                  size="middle">
                                  Guardar
                                </Button>
                              </Col>
                              <Col>
                                <Button
                                  className="cmt-button secondary outline"
                                  type="primary"
                                  size="middle"
                                  onClick={closeEvaluationTypePopover}>
                                  Cancelar
                                </Button>
                              </Col>
                            </Row>
                          </Form>
                        }
                      >
                        <p className="text-center mr-15 mb-0">
                          {
                            (item.evaluation_type || item.evaluation_type === 0) &&
                            evaluationTypes[countryCode].find(type => type.value === item.evaluation_type)?.text
                          }
                        </p>
                      </Popover>
                    </Col>
                    {
                      (laboratoryModule) &&
                      <Col
                        lg={4} xs={12}
                        className="column"
                        style={{ ...alignmentSettings }}
                      >
                        <p className="title text-center mb-5">Laboratorio</p>
                        <p className="text-center mb-0">
                          <Tooltip
                            trigger="click"
                            color="#FFFFFF"
                            style={{ width: 100 }}
                            title={
                              <Row>
                                <Col lg={24} xs={24}>
                                  <p className='title mb-10'>Exámenes</p>
                                </Col>
                                {
                                  item.laboratoryExams?.length > 0 && item.laboratoryExams.map((service, index) => {
                                    return (
                                      <Col lg={24} xs={24} key={index}>
                                        <p className='mb-5'>{service.name}</p>
                                      </Col>
                                    )
                                  })
                                }
                              </Row>
                            }
                          >
                            <p className="text-center mb-0" style={{ cursor: 'pointer' }}>Ver exámenes</p>
                          </Tooltip>
                        </p>
                      </Col>
                    }
                  </Row>
                )
              })
          }
        </Col>   
      }   
    </Row>
  );
};

export default AdmissionDetails;
